import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useToggle } from 'react-use';
import axios from 'axios';
import { Card, Container, Flex, Text, Button, Box } from '../../design-system';
import { matchSorter } from 'match-sorter';
import ContainerHeader from '../../components/ContainerHeader';
import { Table } from '../../components/Table';
import { checkPermissions, errorMsg } from '../../helpers/utils';
import { Session } from '../../contexts/sessionContext';
import { ERR_MSG, PERMISSIONS } from '../../globalConstants';
import { useData } from '../../helpers/data-loader';
import { CONTACT_COLUMNS, CONTACT_FIELDS } from './constants';
import SearchFilter from '../../components/SearchFilter';
import ModalForm from '../../components/ModalForm';
import ExportButton from '../../components/ExportButton';
import Alert from '../../helpers/alert';

const Contacts = () => {
  const navigate = useNavigate();
  const {
    state: {
      user: { permissions },
    },
  } = useContext(Session);
  const canView = useMemo(
    () => checkPermissions(permissions, [PERMISSIONS.READ_CONTACT]),
    [permissions],
  );
  const canCreate = checkPermissions(permissions, [PERMISSIONS.CREATE_CONTACT]);
  const [filter, setFilter] = useState('');

  const [contacts, fetchContacts, loadingContacts] = useData('/contact', {
    errorMsg: errorMsg('Contacts'),
  }) as [any[], any, any];
  const [isModalOpen, toggleModal] = useToggle(false);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const filteredData = useMemo(
    () =>
      matchSorter(contacts, filter, {
        keys: [
          {
            threshold: matchSorter.rankings.CONTAINS,
            key: 'first_name',
          },
          {
            threshold: matchSorter.rankings.CONTAINS,
            key: 'last_name',
          },
          {
            threshold: matchSorter.rankings.CONTAINS,
            key: 'email',
          },
        ],
      }),
    [contacts, filter],
  );

  const handleFilter = (value: string) => {
    setFilter(value);
  };

  const submitNewContact = async (values: any) => {
    try {
      const {
        data: { id },
      } = await axios.post(`/contact`, values);
      toggleModal(false);

      navigate(`/contacts/edit/${id}`);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  return (
    <Container fluid gutter={50}>
      <Card mt={50}>
        <ContainerHeader>
          <Text fontWeight={700} fontSize={25}>
            Contacts
          </Text>
          {canCreate && (
            <Button.Group>
              <Button type="button" onClick={toggleModal}>
                Add
              </Button>
              <Box ml="1rem">
                <ExportButton type="contacts" />
              </Box>
            </Button.Group>
          )}
        </ContainerHeader>
        <Flex justifyContent="start" alignItems="left" p="0 36px 18px 26px">
          <SearchFilter onFilter={handleFilter} />
        </Flex>
        <Table
          name="Contacts"
          columns={CONTACT_COLUMNS}
          data={filteredData.map((item) => ({ ...item, canView }))}
          initialSortBy={[{ id: 'full_name', desc: false }]}
          loading={loadingContacts}
        />
      </Card>
      <ModalForm
        title="Add New Contact"
        isOpen={isModalOpen}
        onSubmit={submitNewContact}
        onClose={toggleModal}
        fields={CONTACT_FIELDS}
        oneColumn
      />
    </Container>
  );
};

export default Contacts;
